import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import '../styles/DesktopPdf.css';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Document, Page, pdfjs } from "react-pdf";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


const PdfContainer = ({text, payslip, downloadPayslip, printPayslip, isDark}) =>{
  const {t} = useTranslation();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [showActionHint, setShowActionHint] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const [showActionBox, setShowActionBox] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [pdfSmall, setPdfSmall] = useState(false);
  const [pageWidth, setPageWidth] = useState(null); // in scale 1
  const [pageHeight, setPageHeight] = useState(null); // in scale 1
  const [zoomLevel, setZoomLevel] = useState(1.5);
  const [maxZoom, setMaxZoom] = useState(1.75);
  const [maxZoomed, setMaxZoomed] = useState(false);
  const zoomedContentRef = useRef(null);

  // Here if anyone wants to add an email to their data. for sending the payroll office a complaint email
  const contactEmail = null; // Get from real data
  const emailSubject = encodeURIComponent(`Help with payslip ${payslip ? format(parseISO(payslip.payment_date), "d.M.yyyy") : ''}`); // Translatable
  const emailBody = encodeURIComponent(`Hello,\n\nI need help with my payslip.\n\nThanks!`); // Translatable

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages); // +1 for error page testing
    setPageNumber(1);
    setShowActionHint(true);
    const timer = setTimeout(() => setShowActionHint(false), 1200); // Show actions on slip select, then fade
    return () => clearTimeout(timer);
  }

  useEffect(() =>{
    const handler = (e) =>{
      if(e.key === "Escape"){
        setZoomed(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  },[]);

  // For showing visual hint for pdf navigation and zoom actions
  useEffect(() =>{
    document.body.style.overflow = zoomed ? 'hidden' : '';
    document.documentElement.style.overflow = zoomed ? 'hidden' : '';
    setZoomLevel(1.5);

    if(zoomed){
      setShowActionBox(true);
      const timer = setTimeout(() => setShowActionBox(false), 2700);
      return () => clearTimeout(timer);
    }
  },[zoomed]);

  // Will run whenever a new page is opened
  useEffect(() =>{
    if(!pageWidth) return;

    // 595 = Test pdf width. Based on the closeBtn placement and arrows being visible
    const scaleDiff = (pageWidth - 595) * zoomLevel;
    const adjWidth = window.innerWidth - scaleDiff;
    
    function getMaxZoom(){
      if(adjWidth <= 1380) return 1.75; // Always allow min 1.75 scale, even if huge pdf. The user can always zoom out :P
      if(adjWidth < 1530) return 2;
      if(adjWidth < 1680) return 2.25;
      if(adjWidth < 1830) return 2.5;
      if(adjWidth < 1980) return 2.75;
      return 3;
    }

    const pageMaxZoom = getMaxZoom();
    setMaxZoom(pageMaxZoom);

  },[pageWidth, zoomLevel]);

  useEffect(()=>{ setZoomLevel(1.5); },[pageNumber]); // reset zoom on new page

  useEffect(() =>{
    if(zoomLevel >= maxZoom) setMaxZoomed(true);
    else setMaxZoomed(false);
    const centeringPoint = window.innerHeight - pageHeight ? 1 : 0.75;
    if(zoomLevel <= centeringPoint || pdfError) setPdfSmall(true);
    else setPdfSmall(false);
  },[zoomLevel, maxZoom, pdfError, pageHeight]);

  const nextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));
  const prevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));

  return(
    <>
      {text ? (
        <div className="infoBox">
          {text === "noData" ?
          <p className="m-0" style={{whiteSpace:"pre-line"}}>{t("errorMessages.noDataContainer")}</p>
          : <p className="m-0">{t("errorMessages.noSlipSelected")}</p>}
        </div>
      ) : (
        <div className={`${zoomed ? 'invisible' : 'd-xxl-flex flex-xxl-row justify-content-xxl-center align-items-xxl-center'}`}>
          <div className="PDFContainer">
            <Document className="PDFDesktop" file={`${payslip.file_path}`} /*  `/api${payslip.file_path}`  */ onLoadSuccess={onDocumentLoadSuccess} error={t("errorMessages.PDFLoad")} loading={t("pdf.PDFLoading")}>
              <div key={pageNumber} className="pdfPageFader">
                <Page className="PDFPageDesktop" pageNumber={pageNumber} scale={0.95} error={t("errorMessages.pageLoad")} loading={t("pdf.pageLoading")} renderAnnotationLayer={false} renderTextLayer={false}/>
              </div>
            </Document>

            {numPages > 1 && pageNumber > 1 && (
              <button data-message={t('pdf.pageBack')} className={`pdfNavZone left ${showActionHint ? 'showAction' : ''} accessibleOpacity`} onClick={prevPage}>
                <i className="bi bi-chevron-left"></i>
              </button>
            )}
            <button data-message={t('pdf.zoomPDF')} className={`zoomHoverBtn ${showActionHint ? 'showAction' : ''}`} onClick={() => setZoomed(true)}>
              <i className="bi bi-zoom-in"></i>
            </button>
            {numPages > 1 && pageNumber < numPages && (
              <button data-message={t('pdf.pageForward')} className={`pdfNavZone right ${showActionHint ? 'showAction' : ''} accessibleOpacity`} onClick={nextPage}>
                <i className="bi bi-chevron-right"></i>
              </button>
            )}

            <p className="mb-0 mt-1">[ {pageNumber} / {numPages} ]</p>
          </div>
          <div className="actionIconBox my-4 mx-auto my-xxl-0 mx-xxl-0 d-flex flex-row align-items-center justify-content-between flex-xxl-column">
            {contactEmail !== null &&
              <a href={`mailto:${contactEmail}?subject=${emailSubject}&body=${emailBody}`}>
                <i aria-hidden="true" className="bi bi-envelope-at"></i>
                <span className={`tooltip ${isDark ? 'fw-bold' : ''}`} aria-hidden="true" tabIndex={-1}>{t("pdf.tooltips.sendEmail")}</span>
                <span className="visually-hidden">{t('pdf.sendEmail')}</span>
              </a>
            }
            <button data-message={t('pdf.printPDF')} onClick={() => printPayslip(`${payslip.file_path}`) /*  `/api${payslip.file_path}`  */}><i className="bi bi-printer"></i><span className={`tooltip ${isDark ? 'fw-bold' : ''}`} aria-hidden="true" tabIndex={-1}>{t("pdf.tooltips.print")}</span></button>
            <button data-message={t('pdf.downloadPDF')} onClick={downloadPayslip}><i className="bi bi-filetype-pdf"></i><span className={`tooltip ${isDark ? 'fw-bold' : ''}`} aria-hidden="true" tabIndex={-1}>{t("pdf.tooltips.download")}</span></button>
            {/*<button data-message={t('pdf.downloadPDF')} onClick={downloadPayslip}><i className="bi bi-download"></i><span className={`tooltip ${isDark ? 'fw-bold' : ''}`} aria-hidden="true" tabIndex={-1}>{t("pdf.tooltips.download")}</span></button>*/}
            <button data-message={t('pdf.zoomPDF')} onClick={() => setZoomed(true)}><i className="bi bi-zoom-in"></i><span className={`tooltip ${isDark ? 'fw-bold' : ''}`} aria-hidden="true" tabIndex={-1}>{t("pdf.tooltips.zoom")}</span></button>
          </div>
        </div>
      )}
      {zoomed && (
        <div className={`pdfZoomBackground ${zoomed ? 'open' : ''} ${!pdfSmall ? 'align-items-start' : 'align-items-stretch '}`} onClick={() => setZoomed(false)}>
          <div className="pdfZoomContent" onClick={(e) => e.stopPropagation()}>
            <button data-message={t('pdf.modalClose')} className="closeBtn" onClick={() => setZoomed(false)}><i className="bi bi-x" aria-hidden="true"></i></button>
            <div className="d-flex flex-direction-row justify-content-center align-items-stretch">
              <div className="col-1 d-flex flex-direction-column justify-content-end align-items-center align-self-end float-end">
                {numPages > 1 && pageNumber > 1 && (
                  <div onClick={prevPage} className="zoomedPageNav left d-flex flex-direction-row justify-content-end align-items-center pe-3">
                    <button data-message={t('pdf.pageBack')} onClick={prevPage}>
                      <i className="bi bi-chevron-left" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
              <div ref={zoomedContentRef} className={`col-auto pdfZoomContainer ${!pdfSmall ? 'pdfModalBoxMargin' : ''} ${pdfError ? 'errorPdf' : ''}`} >
                <Document className="pdfDocumentZoomed" noData={t("errorMessages.PDFLoad")} onLoadSuccess={() => setPdfError(false)} error={t("errorMessages.PDFLoad")} loading={t("pdf.PDFLoading")} onLoadProgress={() => setPdfError(true)}
                  file={`${payslip.file_path}`} /*  `/api${payslip.file_path}`  */ onLoadError={() => setPdfError(true)} onSourceError={() => setPdfError(true)} onSourceSuccess={() => setPdfError(false)}>
                  <div key={pageNumber} className="pdfZoomPageFader">
                    <Page className="pdfPageZoomed" pageNumber={pageNumber} scale={zoomLevel} renderAnnotationLayer={false} renderTextLayer={false} onLoadError={() => setPdfError(true)} noData={t("errorMessages.pageLoad")}
                    error={t("errorMessages.pageLoad")} loading={t("pdf.pageLoading")} onLoadSuccess={(page) => {setPdfError(false); setPageWidth(page.originalWidth); setPageHeight(page.originalHeight);}}/>
                  </div>
                </Document>
              </div>
              <div className={`actionIconBoxZoomed ${showActionBox ? 'showAction' : ''} accessibleOpacity`}>
                {contactEmail !== null &&
                  <a className="actionIconZoomed" href={`mailto:${contactEmail}?subject=${emailSubject}&body=${emailBody}`}>
                    <i aria-hidden="true" className="bi bi-envelope-at"></i>
                    <span className="visually-hidden">{t('pdf.sendEmail')}</span>
                  </a>
                }
                <button data-message={t('pdf.printPDF')} className="actionIconZoomed" onClick={() => printPayslip(`${payslip.file_path}`) /*  `/api${payslip.file_path}`  */}>
                  <i aria-hidden="true" className="bi bi-printer"></i>
                </button>
                <button data-message={t('pdf.downloadPDF')} className="actionIconZoomed" onClick={downloadPayslip}><i className="bi bi-filetype-pdf"></i></button>
                <div className="d-flex flex-direction-row align-items-center justify-content-center gap-4">
                  <button data-message={t("pdf.zoomIn")} className="actionIconZoomed" disabled={maxZoomed}
                    onClick={() => zoomLevel < maxZoom ? setZoomLevel(prev => prev + .25) : null}>
                    <i aria-hidden="true" className="bi bi-zoom-in"></i>
                  </button>
                  <span style={{color:'white', fontSize:'1.6rem', textAlign:'center', width:'5vw'}}>x{zoomLevel}</span>
                  <button data-message={t("pdf.zoomOut")} className="actionIconZoomed" disabled={zoomLevel <= 0.75}
                    onClick={() => setZoomLevel(prev => prev - .25)}>
                    <i aria-hidden="true" className="bi bi-zoom-out"></i>
                  </button>
                </div>
              </div>
              <div className="col-1 d-flex flex-direction-column align-items-center align-self-start float-start">
                {numPages > 1 && pageNumber < numPages && (
                  <div onClick={nextPage} className="zoomedPageNav right d-flex flex-direction-row justify-content-start align-items-center ps-3 ">
                    <button data-message={t('pdf.pageForward')} onClick={nextPage}>
                      <i className="bi bi-chevron-right" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
};

export default PdfContainer;
